import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { workbenchApi } from '../../api/workbench'
import { RefusedError } from '../../api/client'
import { LineIcon } from '../../components/LineIcon'
import { formatDate } from '../../lib/format'
import { useSpoke } from '../../hooks/useSpoke'
import type { MailThread } from '../../types/workbench'
import { channelIcon } from './WorkbenchParts'

// The preview of one past communication (REQ-105): an overlay over the table with the thread's messages in time
// order — who wrote, when, over what, and the words — read from the rep's own stored mail. It closes on Escape, on
// the backdrop, or on its button; it changes nothing: not the draft, not the selection.

export function ThreadPreview({ companyId, personId, thread, onClose }: { companyId: string; personId: string; thread: MailThread; onClose: () => void }) {
  const spoke = useSpoke()
  const q = useQuery({ queryKey: ['workbench-thread', spoke, companyId, personId, thread.subject], queryFn: () => workbenchApi.thread(spoke, companyId, personId, thread.subject), staleTime: 60_000 })
  useEffect(() => { const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }; window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey) }, [onClose])
  const ic = channelIcon(thread.channel)
  return (
    <div className="thread-backdrop" onClick={onClose} role="presentation">
      <div className="thread-overlay" role="dialog" aria-modal="true" aria-label={`${ic.word} thread: ${thread.subject}`} onClick={(e) => e.stopPropagation()}>
        <header className="thread-head">
          <span className={`thread-channel channel-${ic.name}`}><LineIcon name={ic.name} size={20} /> {ic.word}</span>
          <strong className="thread-subject">{thread.subject}</strong>
          <button type="button" className="button small" onClick={onClose}>Close</button>
        </header>
        {q.isPending && <p className="muted small">Reading the thread…</p>}
        {q.isError && <p className="error small">{q.error instanceof RefusedError ? q.error.refusal.message : q.error.message}</p>}
        {q.data && (
          <>
            {q.data.messages.length === 0 && <p className="muted small">Nothing of this thread in your stored mail. Searched {q.data.coverage.addresses.join(', ')} · {formatDate(q.data.coverage.from)} to {formatDate(q.data.coverage.to)}.</p>}
            <ol className="plain thread-messages">
              {q.data.messages.map((m) => (
                <li key={m.id} className={`thread-message ${m.direction}`}>
                  <div className="thread-meta">
                    <strong>{m.direction === 'own' ? 'You' : m.fromName || m.from || 'them'}</strong>
                    {m.from && m.direction !== 'own' && <span className="muted small"> &lt;{m.from}&gt;</span>}
                    <span className="muted small"> · {formatDate(m.sentAt)}</span>
                    {m.to.length > 0 && <span className="muted small"> · to {m.to.join(', ')}</span>}
                  </div>
                  <pre className="thread-text">{m.text}</pre>
                </li>
              ))}
            </ol>
            <p className="muted small">Read from your own stored mail · {q.data.coverage.addresses.join(', ')} · {formatDate(q.data.coverage.from)} to {formatDate(q.data.coverage.to)}.</p>
          </>
        )}
      </div>
    </div>
  )
}
